import { ReactNode, useContext, useMemo } from 'react';
import AdvertiserContext from '../AdvertiserContext';
import { Themes } from '../../constants/themes';

type CopyDictionary = {
  [key: string]: ReactNode;
};

type ThemeValue = (typeof Themes)[keyof typeof Themes];

type CopiesType = Partial<Record<ThemeValue, CopyDictionary>>;

export const useCopy = (copies: CopiesType) => {
  const adContext = useContext(AdvertiserContext);

  return useMemo(() => {
    // provide a fallback to the default theme
    const defaultCopy = copies[Themes.DEFAULT] ?? {};

    const copy = Object.keys(defaultCopy).reduce(
      (acc: CopyDictionary, key) => {
        // We explicitly check for `undefined` here because the theme copy
        // might be `null`, which should be preserved as meaning "don't show
        // any copy here", whereas `undefined` means "use the default copy
        // here"
        acc[key] =
          copies[adContext.theme]?.[key] !== undefined
            ? copies[adContext.theme]?.[key]
            : defaultCopy[key];
        return acc;
      },
      {}
    );
    return copy;
  }, [adContext.theme, copies]);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useCopyNested = <T extends ThemeValue, C = any>(
  copies: Record<T, C>
): C => {
  const adContext = useContext(AdvertiserContext);
  const key = (adContext.theme ?? Themes.DEFAULT) as T;
  return copies[key];
};
